// Utility functions for availability display

const DAYS_ORDER = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
const DAYS_DISPLAY = {
  sunday: 'Sun',
  monday: 'Mon',
  tuesday: 'Tue',
  wednesday: 'Wed',
  thursday: 'Thu',
  friday: 'Fri',
  saturday: 'Sat'
};

const DAYS_FULL = {
  sunday: 'Sunday',
  monday: 'Monday',
  tuesday: 'Tuesday',
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
  saturday: 'Saturday'
};

// Convert 12-hour time to comparable number
const timeToMinutes = (timeStr) => {
  const match = timeStr.match(/(\d+):(\d+)\s*(AM|PM)/i);
  if (!match) return 0;
  let hours = parseInt(match[1]);
  const minutes = parseInt(match[2]);
  const period = match[3].toUpperCase();
  
  if (period === 'PM' && hours !== 12) hours += 12;
  if (period === 'AM' && hours === 12) hours = 0;
  
  return hours * 60 + minutes;
};

// Format time range from slots array
export const formatTimeRange = (slots) => {
  if (!slots || slots.length === 0) return null;
  if (slots.length === 1) return slots[0];
  
  const sortedSlots = [...slots].sort((a, b) => timeToMinutes(a) - timeToMinutes(b));
  
  // Find consecutive ranges
  const ranges = [];
  let rangeStart = sortedSlots[0];
  let lastSlot = sortedSlots[0];
  
  for (let i = 1; i < sortedSlots.length; i++) {
    const currentMin = timeToMinutes(sortedSlots[i]);
    const lastMin = timeToMinutes(lastSlot);
    
    // Check if consecutive (within 1 hour)
    if (currentMin - lastMin > 60) {
      ranges.push(rangeStart === lastSlot ? rangeStart : `${rangeStart} - ${lastSlot}`);
      rangeStart = sortedSlots[i];
    }
    lastSlot = sortedSlots[i];
  }
  ranges.push(rangeStart === lastSlot ? rangeStart : `${rangeStart} - ${lastSlot}`);
  
  return ranges.join(', ');
};

// Get the next available slot from availability data
export const getNextAvailableSlot = (availability) => {
  if (!availability || typeof availability !== 'object') return null;
  
  const now = new Date();
  const currentDay = now.getDay(); // 0 = Sunday
  const currentTime = now.getHours() * 60 + now.getMinutes();
  
  // Check next 7 days
  for (let i = 0; i < 7; i++) {
    const checkDayIndex = (currentDay + i) % 7;
    const dayKey = DAYS_ORDER[checkDayIndex];
    const slots = availability[dayKey];
    
    if (!slots || slots.length === 0) continue;
    
    const sortedSlots = [...slots].sort((a, b) => timeToMinutes(a) - timeToMinutes(b));
    
    for (const slot of sortedSlots) {
      const slotMinutes = timeToMinutes(slot);
      
      // If same day, check if slot is still in future
      if (i === 0 && slotMinutes <= currentTime) continue;
      
      // Calculate the actual date
      const date = new Date(now);
      date.setDate(date.getDate() + i);
      
      return {
        day: DAYS_DISPLAY[dayKey],
        dayFull: DAYS_FULL[dayKey],
        time: slot,
        date: date,
        isToday: i === 0,
        isTomorrow: i === 1,
        daysAway: i
      };
    }
  }
  
  return null;
};

// Format the next available slot for display
export const formatNextAvailable = (availability) => {
  const next = getNextAvailableSlot(availability);
  if (!next) return null;
  
  if (next.isToday) {
    return `Today at ${next.time}`;
  } else if (next.isTomorrow) {
    return `Tomorrow at ${next.time}`;
  } else {
    return `${next.dayFull} at ${next.time}`;
  }
};

// Get availability for display (organized by day)
export const getAvailabilityDisplay = (availability) => {
  if (!availability || typeof availability !== 'object') return [];
  
  return DAYS_ORDER
    .filter(day => availability[day] && availability[day].length > 0)
    .map(day => ({
      key: day,
      day: DAYS_DISPLAY[day],
      dayFull: DAYS_FULL[day],
      slots: availability[day],
      timeRange: formatTimeRange(availability[day])
    }));
};

export default {
  getNextAvailableSlot,
  formatNextAvailable,
  getAvailabilityDisplay,
  formatTimeRange
};
